import { useEffect, useState } from "react";
import { storeTargetFor, type StoreTarget } from "@/lib/platform";
import { storeLocationPhrase, storeNameFor, storeUrlFor } from "@/lib/storeLinks";

/**
 * Soft, dismissable popup that nudges MOBILE BROWSER visitors to install the app.
 *
 * Gated to ONLY show when:
 *   - a mobile browser, iOS or Android (`storeTargetFor` returns non-null)
 *   - NOT inside the Median shell — she already has the app
 *   - User hasn't dismissed before (localStorage flag)
 *   - User has already seen the splash screen (so we don't compete visually
 *     with first-run splash → signin flow)
 *
 * Visual pattern modeled on HowDoesThisWorkTooltip.tsx.
 *
 * ⚠️ Sep 2026 (#1703) — ANDROID WAS GETTING NOTHING. This was iOS-only, and the
 * repo had no Play Store link anywhere, so an Android web visitor saw no nudge at
 * all. That is half the population this exists for: web signups do not stick (7
 * all-time, 0 active in the last 7 days, against 43 App Store signups with 11 still
 * active), and a browser signup has no icon and no push to bring her back.
 *
 * ⚠️ It also carried its OWN UA detection: two inline regexes, one for Apple
 * devices and one for the Median shell. That is the #355 shape exactly — two copies
 * of platform detection, which diverged once already and recorded every Android
 * session as iOS for eleven weeks. (Described rather than reproduced, so a guard
 * scanning this file for an inline detector cannot trip on the prose explaining why
 * there isn't one.)
 * Detection now comes from `platform.ts` (`storeTargetFor`), the one home for it.
 */

// ⚠️ UNCHANGED, and it must stay unchanged: `e2e/helpers.ts` pre-dismisses this
// popup by writing this exact key, so renaming it silently un-dismisses the nudge
// in every spec that signs in and it starts intercepting clicks again (#286's shape).
const STORAGE_KEY = "kin_app_store_nudge_dismissed";
const SPLASH_KEY = "kin_splash_seen";
const SHOW_DELAY_MS = 1500;

const AppStoreNudge = () => {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<StoreTarget>(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return;

    // null covers the Median shell, desktop, and an unreadable UA — see
    // storeTargetFor. Three different reasons to show nothing, one answer.
    const resolved = storeTargetFor(navigator.userAgent);
    if (!resolved) return;

    const dismissed = localStorage.getItem(STORAGE_KEY) === "true";
    const splashSeen = localStorage.getItem(SPLASH_KEY) === "true";
    if (dismissed || !splashSeen) return;

    setTarget(resolved);
    const t = window.setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const persistDismiss = () => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, "true");
      } catch {
        /* ignore */
      }
    }
  };

  const dismiss = () => {
    persistDismiss();
    setOpen(false);
  };

  if (!open || !target) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Kin is ${storeLocationPhrase(target)}`}
      onClick={dismiss}
      className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in"
      style={{
        backgroundColor: "rgba(28, 26, 62, 0.45)",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="animate-scale-in"
        style={{
          backgroundColor: "#E8E8F4",
          borderRadius: 16,
          padding: 24,
          boxShadow: "0 12px 32px rgba(28, 26, 62, 0.2)",
          maxWidth: 340,
          width: "100%",
        }}
      >
        <h2
          className="font-display"
          style={{
            fontSize: 22,
            color: "#1C1A3E",
            margin: 0,
            lineHeight: 1.25,
          }}
        >
          Kin's {storeLocationPhrase(target)}! 💜
        </h2>
        <p
          className="font-body"
          style={{
            fontSize: 14,
            color: "#54525C",
            lineHeight: 1.5,
            marginTop: 12,
            marginBottom: 20,
          }}
        >
          Hey mama, Kin's now {storeLocationPhrase(target)}! Download the app for the best experience.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <a
            href={storeUrlFor(target)}
            onClick={persistDismiss}
            className="font-body"
            style={{
              display: "block",
              width: "100%",
              textAlign: "center",
              backgroundColor: "#5050C8",
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: 14,
              padding: "14px 16px",
              borderRadius: 12,
              textDecoration: "none",
            }}
          >
            Open {storeNameFor(target)}
          </a>
          <button
            type="button"
            onClick={dismiss}
            className="font-body"
            style={{
              width: "100%",
              backgroundColor: "transparent",
              color: "#54525C",
              fontWeight: 500,
              fontSize: 14,
              padding: "12px 16px",
              border: "none",
              borderRadius: 12,
              cursor: "pointer",
            }}
          >
            Continue in browser
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppStoreNudge;
